import { CheckIcon, SparklesIcon, CubeIcon } from "@heroicons/react/24/outline"
import React, { useState } from "react"

import { Button } from "../../components/shared/Buttons"
import Typography from "../../components/shared/Typography"
import { useToast } from "../../contexts/ToastContext"

const BuilderVersionSettings = ({ practice: initialPractice }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [practice, setPractice] = useState(initialPractice)
  const { showToast } = useToast()

  // Check if practice was created before the configured cutoff date
  const cutoffDate = new Date(window.App?.builder_version_cutoff_date || "2025-09-01")
  const practiceCreatedAt = practice?.createdAt ? new Date(practice.createdAt) : null
  const shouldShowBuilderVersionOptions = practiceCreatedAt && practiceCreatedAt < cutoffDate

  const switchBuilderVersion = async (version) => {
    setIsLoading(true)
    try {
      const response = await fetch(`/portal/builder_version/switch_to_${version}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content")
        }
      })

      if (response.ok) {
        // Update local state and reload the page to refresh all settings
        setPractice({ ...practice, builderVersion: version, usingBuilderV2: version === "v2" })
        showToast(`Public website now shows Page Builder ${version.toUpperCase()} version!`, "success")

        // Reload the page after a short delay to allow the toast to show
        setTimeout(() => {
          window.location.reload()
        }, 100)
      } else {
        showToast("Failed to switch page builder version", "error")
      }
    } catch (error) {
      showToast("Error switching page builder version", "error")
    } finally {
      setIsLoading(false)
    }
  }

  // Don't render the component if practice was created after the cutoff date
  if (!shouldShowBuilderVersionOptions) {
    return null
  }

  return (
    <div
      className="space-y-8 p-6"
      style={{
        width: "100%",
        maxWidth: "none",
        minWidth: "100%"
      }}>
      <div className="text-center">
        <Typography variant="h5" className="text-gray-900">
          Public Website Version
        </Typography>
        <Typography variant="subtitle" className="mt-2 text-gray-600">
          Choose which page builder version visitors will see when they visit your practice pages.
        </Typography>
      </div>

      <div
        className="w-full"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "2rem",
          width: "100%",
          maxWidth: "none"
        }}>
        {/* Page Builder v1 Card */}
        <div
          className={`relative overflow-hidden rounded-2xl border transition-all duration-300 ${
            !practice?.usingBuilderV2
              ? "border-blue-500 bg-gradient-to-br from-blue-50 shadow-lg ring-1 ring-blue-500/20"
              : "border-gray-200 bg-white shadow-sm hover:border-gray-300 hover:shadow-md"
          }`}>
          {/* Header */}
          <div className="p-6 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100">
                  <CubeIcon className="h-6 w-6 text-gray-600" />
                </div>
                <div className="ml-4">
                  <Typography variant="h5" className="font-bold text-gray-900">
                    Page Builder v1
                  </Typography>
                  <Typography variant="smSubtitle" className="text-gray-500">
                    Available
                  </Typography>
                </div>
              </div>

              {!practice?.usingBuilderV2 && (
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500">
                  <CheckIcon className="h-4 w-4 text-white" />
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="px-6 pb-6">
            <Typography variant="body" className="mb-6 leading-relaxed text-gray-600">
              Show visitors your website built with the original page builder. Stable and familiar interface.
            </Typography>

            {!practice?.usingBuilderV2 && (
              <div className="mb-4 rounded-lg bg-blue-300 p-3">
                <Typography variant="body" className="font-medium text-blue-800">
                  ✨ Currently Live
                </Typography>
                <Typography variant="small" className="text-blue-700">
                  This is what visitors see on your public website
                </Typography>
              </div>
            )}

            <div className="space-y-3">
              <a href="/portal/website/builder" target="_blank" rel="noopener noreferrer">
                <Button type="tertiary" className="w-full">
                  Edit with v1 Page Builder
                </Button>
              </a>

              {practice?.usingBuilderV2 && (
                <Button
                  type="primary"
                  onClick={() => switchBuilderVersion("v1")}
                  disabled={isLoading}
                  className="w-full"
                  style={{
                    backgroundColor: "#3b82f6",
                    borderColor: "#3b82f6",
                    color: "white"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#2563eb"
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#3b82f6"
                  }}>
                  {isLoading ? "Switching..." : "Make Live"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Page Builder v2 Card */}
        <div
          className={`relative overflow-hidden rounded-2xl border transition-all duration-300 ${
            practice?.usingBuilderV2
              ? "border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 shadow-lg ring-1 ring-purple-500/20"
              : "border-gray-200 bg-white shadow-sm hover:border-gray-300 hover:shadow-md"
          }`}>
          {/* New Badge */}
          <div className="absolute right-4 top-4">
            <span className="inline-flex items-center rounded-full bg-gradient-to-r from-purple-500 to-pink-500 px-3 py-1 text-xs font-semibold text-white">
              ✨ New
            </span>
          </div>

          {/* Header */}
          <div className="p-6 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-purple-100 to-pink-100">
                  <SparklesIcon className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <Typography variant="h5" className="font-bold text-gray-900">
                    Page Builder v2
                  </Typography>
                  <Typography variant="smSubtitle" className="font-medium text-purple-600">
                    New!
                  </Typography>
                </div>
              </div>

              {practice?.usingBuilderV2 && (
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-500">
                  <CheckIcon className="h-4 w-4 text-white" />
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="px-6 pb-6">
            <Typography variant="body" className="mb-6 leading-relaxed text-gray-600">
              Show visitors your website built with the new page builder. Modern design and enhanced features.
            </Typography>

            {practice?.usingBuilderV2 && (
              <div className="mb-4 rounded-lg bg-purple-100 p-3">
                <Typography variant="body" className="font-medium text-purple-800">
                  ✨ Currently Live
                </Typography>
                <Typography variant="small" className="text-purple-700">
                  This is what visitors see on your public website
                </Typography>
              </div>
            )}

            <div className="space-y-3">
              <a href="/portal/website_v2/builder" target="_blank" rel="noopener noreferrer">
                <Button type="tertiary" className="w-full">
                  Edit with v2 Page Builder
                </Button>
              </a>

              {!practice?.usingBuilderV2 && (
                <Button
                  type="primary"
                  onClick={() => switchBuilderVersion("v2")}
                  disabled={isLoading}
                  className="w-full"
                  style={{
                    background: "linear-gradient(to right, #9333ea, #e11d48)",
                    borderColor: "transparent",
                    color: "white"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "linear-gradient(to right, #7c3aed, #be185d)"
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "linear-gradient(to right, #9333ea, #e11d48)"
                  }}>
                  {isLoading ? "Switching..." : "Make Live"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status Footer */}
      <div className="rounded-xl bg-gray-50 p-4">
        <div className="flex items-start">
          <div className="flex h-5 w-5 items-center justify-center">
            <div className={`h-2 w-2 rounded-full ${practice?.usingBuilderV2 ? "bg-purple-500" : "bg-blue-500"}`}></div>
          </div>
          <div className="ml-3">
            <Typography variant="body" className="font-medium text-gray-700">
              Public website shows:{" "}
              <span className={practice?.usingBuilderV2 ? "text-purple-600" : "text-blue-600"}>
                Page Builder {practice?.usingBuilderV2 ? "V2" : "V1"} version
              </span>
            </Typography>
            <Typography variant="small" className="mt-1 text-gray-500">
              Both page builders remain available for editing • Changes only affect your public website
            </Typography>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BuilderVersionSettings
